import { Updater } from '@tanstack/react-form';
import { useQuery } from '@tanstack/react-query';
import { UserRole } from '@thedealersconcierge/lib/codecs/tdc';
import { FC, useState } from 'react';
import employeeSelector from '~/selectors/employeeSelector';
import { Pagination } from '../../_dashboardUtils';

import {
  Typeahead,
  TypeaheadOption
} from '@thedealersconcierge/components/src/input/typeahead';
import classNames from 'classnames';
import { getReadableRole } from '~/lib/enumReadable';

/**
 * TODO: Update to new tanstack library
 */
const UserDropDownField: FC<{
  // This needs to be null for onClear event
  // Tanstack doesn't support undefined for clearing input, it will revert to default value
  value: string | null;
  handleChange: (updater: Updater<string | null>) => void;
  labeltext?: string;
  roleToDisplay: UserRole;
  disabled?: boolean;
  initialSearchName?: string;
  dealershipId?: string;
  className?: string;
  dataTestId?: string;
  backgroundType?: 'LIGHT' | 'DARK';
}> = ({
  value,
  handleChange,
  labeltext,
  roleToDisplay,
  disabled = false,
  initialSearchName,
  dealershipId,
  className,
  dataTestId,
  backgroundType
}) => {
  // This tracks initial displayed user, and increment per display more
  const NUMBER_OF_USER_DISPLAYED = 5;
  const [page, setPage] = useState<Pagination>({
    currentPage: 1,
    direction: 'after',
    pageSize: 10
  });

  const [searchName, setSearchName] = useState<string | undefined>(
    initialSearchName
  );
  const { data, isLoading } = useQuery(
    employeeSelector(roleToDisplay, dealershipId, page, searchName)
  );

  const userOptions: TypeaheadOption<string>[] =
    data?.dealership?.users?.edges?.map((e) => ({
      key: e.node?.user?.id ?? 'no-key',
      label: `${e.node?.user?.firstName ?? ''} ${e.node?.user?.lastName ?? ''}`,
      subtext: e.node?.role ? getReadableRole(e.node?.role) : '',
      value: e.node?.user?.id ?? 'no-value'
    })) ?? [];

  const selectedOption = value
    ? userOptions.find((opt) => opt.value === value)
    : undefined;

  const handleSelect = (selected: TypeaheadOption<string>) => {
    handleChange(selected.value);
  };

  return (
    <div
      className={classNames(
        'flex flex-row justify-between gap-4 items-start',
        className
      )}
    >
      <div className="grow">
        <Typeahead<string>
          disabled={disabled}
          label={labeltext}
          placeHolder="Select"
          required={false}
          key={`user_dropdown_for_role_${roleToDisplay}`}
          options={userOptions}
          onSelect={handleSelect}
          onClear={() => {
            // This took me a a while to debug, this behavior is so implicit
            // Turns out if we pass `undefined` to tanstack `field.handleChange` it will revert to default value instead of emptying it
            // https://github.com/TanStack/form/issues/1614
            handleChange(null);
            setSearchName('');
          }}
          onSearch={(searchString: string) => {
            setSearchName(searchString);
          }}
          displayItemCount={NUMBER_OF_USER_DISPLAYED}
          displayMoreType="WHEN_HAVE_REMAINING"
          displayMoreHandler={() => {
            setPage((prev) => {
              return {
                ...prev,
                pageSize: prev.pageSize + NUMBER_OF_USER_DISPLAYED
              };
            });
          }}
          title={labeltext}
          isLoading={isLoading}
          value={selectedOption}
          dataTestId={dataTestId}
          debounceTimeInMs={200}
          hideAssistiveMessage={true}
          backgroundType={backgroundType}
        />
      </div>
    </div>
  );
};

export default UserDropDownField;
